import React, { useEffect, useState, useMemo } from 'react';
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { Event, EventType, getEventTypeLabel, eventGroupLabel } from '../../types/event';
import { eventsAPI, ageGroupsAPI } from '../../services/api';

type EventTypeFilter = EventType | 'all';

interface Filters {
  type: EventTypeFilter;
  ageGroup: string;
  startDate: string;
  endDate: string;
}

const DEFAULT_DAYS = 60; // hány napra előre kérjük le az eseményeket

const UpcomingEventsList: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(() => {
    const today = new Date();
    const inThirtyDays = new Date();
    inThirtyDays.setDate(inThirtyDays.getDate() + 30);

    return {
      type: 'all',
      ageGroup: '',
      startDate: today.toISOString().split('T')[0],
      endDate: inThirtyDays.toISOString().split('T')[0],
    };
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await eventsAPI.getUpcoming(DEFAULT_DAYS);
        setEvents(data);
      } catch (err) {
        console.error(err);
        setError('Hiba történt a közelgő események betöltésekor.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const [ageGroupOptions, setAgeGroupOptions] = useState<string[]>([]);

  useEffect(() => {
    const loadAgeGroups = async () => {
      try {
        const data = await ageGroupsAPI.getForSelect();
        setAgeGroupOptions(data.map((ag) => ag.name));
      } catch (err) {
        console.error('Failed to load age groups:', err);
      }
    };

    loadAgeGroups();
  }, []);

  const handleFilterChange = (partial: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
  };

  const filteredEvents = useMemo(() => {
    const { type, ageGroup, startDate, endDate } = filters;
    const start =
      startDate && !Number.isNaN(Date.parse(startDate))
        ? new Date(startDate)
        : null;
    const end =
      endDate && !Number.isNaN(Date.parse(endDate)) ? new Date(endDate) : null;

    return events
      .filter((e) => {
        if (type !== 'all' && e.type !== type) return false;
        if (ageGroup && e.ageGroup !== ageGroup) return false;

        const eventDate = new Date(e.startTime);
        if (start && eventDate < start) return false;
        if (end) {
          const endOfDay = new Date(end);
          endOfDay.setHours(23, 59, 59, 999);
          if (eventDate > endOfDay) return false;
        }

        return true;
      })
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
  }, [events, filters]);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
    });

  const formatTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${start.toLocaleTimeString('hu-HU', {
      hour: '2-digit',
      minute: '2-digit',
    })} - ${end.toLocaleTimeString('hu-HU', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Szűrők */}
      <div className="border-b bg-white px-4 sm:px-6 py-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4 sm:items-end">
        <div className="flex items-center gap-2 text-gray-700">
          <FunnelIcon className="h-5 w-5" />
          <span className="font-medium text-sm">Szűrők</span>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:gap-4">
          {/* Típus szűrő */}
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">
              Típus
            </label>
            <select
              className="border-gray-300 rounded-md text-sm w-full sm:w-auto"
              value={filters.type}
              onChange={(e) =>
                handleFilterChange({
                  type: e.target.value === 'all'
                    ? 'all'
                    : (Number(e.target.value) as EventType),
                })
              }
            >
              <option value="all">Mindegy</option>
              <option value={EventType.Training}>
                {getEventTypeLabel(EventType.Training)}
              </option>
              <option value={EventType.Match}>
                {getEventTypeLabel(EventType.Match)}
              </option>
              <option value={EventType.Meeting}>
                {getEventTypeLabel(EventType.Meeting)}
              </option>
              <option value={EventType.Other}>
                {getEventTypeLabel(EventType.Other)}
              </option>
            </select>
          </div>

          {/* Korosztály szűrő */}
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">
              Korosztály
            </label>
            <select
              className="border-gray-300 rounded-md text-sm sm:min-w-[140px] w-full sm:w-auto"
              value={filters.ageGroup}
              onChange={(e) =>
                handleFilterChange({ ageGroup: e.target.value })
              }
            >
              <option value="">Mindegy</option>
              {ageGroupOptions.map((ag) => (
                <option key={ag} value={ag}>
                  {ag}
                </option>
              ))}
            </select>
          </div>

          {/* Dátum intervallum */}
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">
              Kezdő dátum
            </label>
            <input
              type="date"
              className="border-gray-300 rounded-md text-sm w-full sm:w-auto"
              value={filters.startDate}
              onChange={(e) =>
                handleFilterChange({ startDate: e.target.value })
              }
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">
              Záró dátum
            </label>
            <input
              type="date"
              className="border-gray-300 rounded-md text-sm w-full sm:w-auto"
              value={filters.endDate}
              onChange={(e) =>
                handleFilterChange({ endDate: e.target.value })
              }
            />
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <CalendarIcon className="h-12 w-12 text-gray-400 mb-2" />
            <p>Nincs a szűrőknek megfelelő közelgő esemény.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredEvents.map((event) => (
              <li
                key={event.id}
                className="bg-white px-4 sm:px-6 py-4 hover:bg-gray-50 transition"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        {getEventTypeLabel(event.type)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {eventGroupLabel(event)}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      {event.title}
                    </h3>
                    {event.description && (
                      <p className="mt-1 text-xs text-gray-600 line-clamp-2 max-w-xl">
                        {event.description}
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 sm:text-right">
                    <div className="flex items-center sm:justify-end gap-1">
                      <CalendarIcon className="h-4 w-4" />
                      <span>{formatDate(event.startTime)}</span>
                    </div>
                    <div className="flex items-center sm:justify-end gap-1 mt-1">
                      <ClockIcon className="h-4 w-4" />
                      <span>{formatTime(event.startTime, event.endTime)}</span>
                    </div>
                    {(event.location || event.venue) && (
                      <div className="flex items-center sm:justify-end gap-1 mt-1">
                        <MapPinIcon className="h-4 w-4" />
                        <span className="truncate max-w-[200px]">
                          {event.venue?.name || event.location}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UpcomingEventsList;

